if (!customElements.get('personalization-field')) {
  const NAME_PATTERN = /^[A-Za-z]{1,12}$/;
  const MAX_LENGTH = 12;
  const ERROR_EMPTY = 'Please enter a name';
  const ERROR_INVALID = 'Enter a single name — letters only, up to 12 characters.';

  customElements.define(
    'personalization-field',
    class PersonalizationField extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
      }

      connectedCallback() {
        if (!this.input) return;

        this.mode = this.dataset.mode;
        this.optionIndex = this.dataset.optionIndex;
        this.form = this.input.form;

        this.onInput = this.onInput.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('blur', this.onBlur);
        // Capture phase on document: guaranteed to run before product-form's own
        // submit handler (which is attached directly on the form), so we can
        // block the add-to-cart fetch before it fires. Works for both the regular
        // Add to cart button and the dynamic-checkout/Buy now button, since both
        // submit the same form.
        document.addEventListener('submit', this.onSubmit, true);

        if (this.mode === 'optional') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.getSectionId()) return;
            this.applyVariant(event.data.variant);
          });

          this.applyVariant(this.getSelectedVariantFromDom());
        }
      }

      disconnectedCallback() {
        this.input?.removeEventListener('input', this.onInput);
        this.input?.removeEventListener('blur', this.onBlur);
        document.removeEventListener('submit', this.onSubmit, true);
        this.unsubscribeVariantChange?.();
      }

      // Resolved dynamically from the nearest product-info ancestor rather than a
      // static value stamped at render time. Quick-add's modal rewrites section ids
      // to avoid duplicate DOM ids, which broke a previous version of this component
      // that trusted its own stale copy of the id — reading it fresh from product-info
      // each time keeps this correct in both the full product page and the modal.
      getSectionId() {
        const productInfo = this.closest('product-info');
        return productInfo?.dataset.originalSection || productInfo?.dataset.section;
      }

      getSelectedVariantFromDom() {
        const script = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return null;
        }
      }

      applyVariant(variant) {
        if (!variant || !this.input || !this.optionIndex) return;

        const isNameTagSelected = variant[`option${this.optionIndex}`] === 'With name tag';

        this.classList.toggle('hidden', !isNameTagSelected);
        this.input.disabled = !isNameTagSelected;

        if (!isNameTagSelected) {
          this.input.value = '';
          this.clearError();
        }
      }

      onInput() {
        const raw = this.input.value;
        const sanitized = raw.replace(/[^A-Za-z]/g, '').slice(0, MAX_LENGTH);

        if (sanitized !== raw) {
          this.input.value = sanitized;
          this.showError(ERROR_INVALID);
        } else {
          this.clearError();
        }
      }

      onBlur() {
        this.input.value = this.input.value.trim();
      }

      onSubmit(evt) {
        if (evt.target !== this.form) return;
        if (this.input.disabled) return;

        const value = this.input.value.trim();
        this.input.value = value;

        if (value === '') {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.showError(ERROR_EMPTY);
          this.input.focus();
          return;
        }

        if (!NAME_PATTERN.test(value)) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.showError(ERROR_INVALID);
          this.input.focus();
          return;
        }

        this.clearError();
      }

      showError(message) {
        this.closest('product-info')?.querySelector('product-form')?.handleErrorMessage?.(message);
      }

      clearError() {
        this.closest('product-info')?.querySelector('product-form')?.handleErrorMessage?.();
      }
    }
  );
}
